import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase-config.js';

/**
 * Paid tipping — "Tip the DJ" as a real USD 1.00 PayPal payment.
 *
 * Per-venue, because most channels keep the free symbolic tip: a venue only
 * takes money when users/{venueUid}.tipsPaidEnabled is true. Chic Chic Bar has
 * it on for their live stream; everyone else is unaffected.
 *
 * The amount and the recipient both live on the server (paypalTips) — this file
 * only drives the UI. Nothing here decides what anything costs.
 */

const API_BASE = 'https://us-central1-chatbout001.cloudfunctions.net/paypalTips';

/** Is this channel taking paid tips? Defaults to false — money is opt-in. */
export const paidTipsEnabled = async (venueUid) => {
  if (!venueUid) return false;
  try {
    const snap = await getDoc(doc(db, 'users', venueUid, 'settings', 'tipping'));
    return snap.exists() && snap.data()?.paidEnabled === true;
  } catch (_) {
    return false;
  }
};

/**
 * Live-watch the flag so flipping the toggle in the dashboard reaches guests
 * already holding the page open — no reload, no "why is it still free?".
 *
 * ⚠ WATCHES A DEDICATED DOC, NOT users/{venueUid}. The venue doc is a HOT
 * document: playcount-functions.js rewrites `playCounts`/`playCountMeta` on it
 * on EVERY track play, and it has grown to ~310 KB (449 tracks of history).
 * A listener on it would re-deliver all 310 KB to every guest on every song —
 * roughly 2,700 reads and 800 MB a night for a 40-guest venue, to learn a flag
 * that changed zero times. This doc only changes when someone flips the switch,
 * so the steady-state cost is one small read per guest per session.
 *
 * An ABSENT doc means off, which is also the default — so there is no fallback
 * read of the venue doc, and no cost for the venues that never enable this.
 *
 * Falls closed on error: if we cannot read the flag we show the FREE tip, never
 * a payment prompt we are not sure the venue asked for.
 */
export const subscribePaidTips = (venueUid, cb) => {
  if (!venueUid) { cb(false); return () => {}; }
  return onSnapshot(
    doc(db, 'users', venueUid, 'settings', 'tipping'),
    (snap) => cb(snap.exists() && snap.data()?.paidEnabled === true),
    (e) => { console.warn('[tipPayment] flag listener:', e?.message); cb(false); }
  );
};

let sdkPromise = null;

/**
 * Load the PayPal SDK once per page.
 *
 * Cached as a promise, not a boolean: two guests tapping Tip in quick
 * succession would otherwise start two script loads and race.
 */
export const loadPayPalSdk = async () => {
  if (window.paypal) return window.paypal;
  if (sdkPromise) return sdkPromise;

  sdkPromise = (async () => {
    const cfg = await fetch(`${API_BASE}/api/config`).then((r) => r.json());
    if (!cfg?.clientId) throw new Error('PayPal is not configured');
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(cfg.clientId)}`
        + `&currency=${encodeURIComponent(cfg.currency || 'USD')}&intent=capture`;
      s.onload = resolve;
      s.onerror = () => reject(new Error('Could not load PayPal'));
      document.head.appendChild(s);
    });
    return window.paypal;
  })();

  try {
    return await sdkPromise;
  } catch (e) {
    sdkPromise = null;   // let a later tap retry after a flaky network
    throw e;
  }
};

/** Ask the server to open an order. Returns the PayPal order id. */
export const createTipOrder = async (venueUid) => {
  const res = await fetch(`${API_BASE}/api/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ venueUid }),
  });
  const data = await res.json();
  if (!res.ok || !data?.id) throw new Error(data?.error || 'Could not start the payment');
  return data.id;
};

/**
 * Capture an approved order. The server takes the money and records the tip.
 * Returns true only on a COMPLETED capture — the caller must not celebrate
 * (or increment anything) on anything less.
 */
export const captureTipOrder = async (orderId, venueUid, fromUid) => {
  const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderId)}/capture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ venueUid, fromUid: fromUid || null }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'The payment could not be completed');
  return String(data?.status).toUpperCase() === 'COMPLETED';
};
